// frontend/src/services/api.js
import axios from 'axios';

const API_BASE_URL = 'https://medigo-api.onrender.com/api'; 

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('medigo_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

const apiService = {

  get: async (endpoint, config = {}) => {
    try {
      const response = await api.get(endpoint, config);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error(error.response?.data?.message || `Failed to fetch ${endpoint}`);
    }
  },

  post: async (endpoint, data, config = {}) => {
    try {
      const response = await api.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error(error.response?.data?.message || `Failed to post to ${endpoint}`);
    }
  },

  put: async (endpoint, data, config = {}) => {
    try {
      const response = await api.put(endpoint, data, config);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error(error.response?.data?.message || `Failed to update ${endpoint}`);
    }
  },

  patch: async (endpoint, data, config = {}) => {
    try {
      const response = await api.patch(endpoint, data, config);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error(error.response?.data?.message || `Failed to update ${endpoint}`);
    }
  },

  delete: async (endpoint, config = {}) => {
    try {
      const response = await api.delete(endpoint, config);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error(error.response?.data?.message || `Failed to delete ${endpoint}`);
    }
  },

  auth: {
    register: async (userData) => {
      try {
        const response = await api.post('/auth/register', userData);
        return response.data;
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          throw new Error('Registration timed out. The server may be starting up. Please wait a moment and try again.');
        }
        if (error.code === 'ERR_NETWORK') {
          throw new Error('Network error. Please check your connection.');
        }
        throw new Error(error.response?.data?.message || 'Registration failed');
      }
    },

    getHospitalsForRegistration: async (search = '', limit = 50) => {
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        params.append('limit', limit);
        
        const response = await api.get(`/auth/hospitals/for-registration?${params}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    login: async (credentials) => {
      try {
        const response = await api.post('/auth/login', credentials);  
        return response.data;
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          throw new Error('Login timed out. The server may be starting up (this can take 1-2 minutes on first request). Please wait a moment and try again.');
        }
        if (error.code === 'ERR_NETWORK') {
          throw new Error('Network error. Please check your connection and ensure the backend server is running.');
        }
        throw new Error(error.response?.data?.message || 'Login failed');
      }
    },

    getCurrentUser: async () => {
      try {
        const response = await api.get('/auth/me');
        return response.data;
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
        }
        if (error.code === 'ERR_NETWORK') {
          throw new Error('Network error. Please check your connection.');
        }
        throw new Error(error.response?.data?.message || 'Failed to fetch user data');
      }
    },

    changePassword: async (passwordData) => {
      try {
        const response = await api.put('/auth/change-password', passwordData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to change password');
      }
    },

    forgotPassword: async (data) => {
      try {
        const response = await api.post('/auth/forgot-password', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to send reset instructions');
      }
    },

    verifyOTP: async (data) => {
      try {
        const response = await api.post('/auth/verify-otp', data);
        return response.data;
      } catch (error) {
        if (error.response?.data) {
          return {
            success: false,
            message: error.response.data.message || 'OTP verification failed'
          };
        }
        throw error;
      }
    },

    resetPassword: async (data) => {
      try {
        const response = await api.post('/auth/reset-password', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to reset password');
      }
    },

    verifyEmail: async (otp) => {
      try {
        const response = await api.post('/auth/verify-email', { otp });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Verification failed');
      }
    },

    resendVerification: async (data) => {
      try {
        const response = await api.post('/auth/resend-verification', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to send verification code');
      }
    },

    refreshToken: async () => {
      try {
        const response = await api.post('/auth/refresh');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to refresh token');
      }
    },

    logout: async () => {
      try {
        const response = await api.post('/auth/logout');
        return response.data;
      } catch (error) {
        return { success: true, message: 'Logged out' };
      }
    }
  },

 users: {
    getProfile: async () => {
      try {
        const response = await api.get('/users/profile');
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return {
            success: false,
            message: 'Hospital profile not found',
            data: null,
            needsRegistration: true
          };
        }
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          return {
            success: false,
            message: 'Unable to load hospital profile. Please check your connection.',
            data: null,
            networkError: true,
            canRetry: true
          };
        }

        throw new Error(error.response?.data?.message || 'Failed to fetch hospital profile');
      }
    },

    updateProfile: async (profileData) => {
      try {
        const response = await api.put('/users/profile', profileData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update profile');
      }
    },

    uploadAvatar: async (formData) => {
      try {
        const response = await api.post('/users/avatar', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to upload avatar');
      }
    },

    deleteAccount: async () => {
      try {
        const response = await api.delete('/users/account');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete account');
      }
    }
  }, 

  hospitals: {
   getProfile: async () => {
    try {
      const response = await api.get('/hospitals/profile');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Hospital profile not found',
          data: null,
          needsRegistration: true
        };
      }
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: 'Unable to load hospital profile. Please check your connection.',
          data: null,
          networkError: true
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital profile');
    }
  },

  getSettings: async () => {
    try {
      const response = await api.get('/hospitals/settings');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Hospital profile not found',
          data: null,
          needsRegistration: true
        };
      }
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: 'Unable to load hospital settings. Please check your connection.',
          data: null,
          networkError: true,
          canRetry: true
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital settings');
    }
  },

  updateSettings: async (settingsData) => {
    try {
      const response = await api.put('/hospitals/settings', settingsData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Hospital profile not found. Please complete registration first.');
      }
      throw new Error(error.response?.data?.message || 'Failed to update hospital settings');
    }
  },

  updateProfileImage: async (formData) => {
  try {
    const response = await api.put('/hospitals/settings/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update profile image');
  }
},

  deleteImage: async (imageIndex) => {
    try {
      const response = await api.delete(`/hospitals/settings/images/${imageIndex}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete image');
    }
  },

  setPrimaryImage: async (imageIndex) => {
    try {
      const response = await api.patch(`/hospitals/settings/images/${imageIndex}/primary`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to set primary image');
    }
  },
 getServices: async (id) => {
    try {
      const response = await api.get(`/hospitals/${id}/services`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital services');
    }
  },
  getDoctors: async (id, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/hospitals/${id}/doctors${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital doctors');
    }
  },
  getAmbulances: async (id, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/hospitals/${id}/ambulances${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital ambulances');
    }
  },
  getReviews: async (id, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/hospitals/${id}/reviews${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital reviews');
    }
  },
  getStats: async (id) => {
    try {
      const response = await api.get(`/hospitals/${id}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital statistics');
    }
  },
     getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/hospitals${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospitals');
    }
  },

  getById: async (id, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/hospitals/${id}${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch hospital');
    }
  },

   getNearby: async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/hospitals/nearby/search?${queryString}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch nearby hospitals');
  }
},

    search: async (query, filters = {}) => {
      try {
        const params = new URLSearchParams({ q: query, ...filters });
        const response = await api.get(`/hospitals/search?${params}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to search hospitals');
      }
    },

    create: async (hospitalData) => {
      try {
        const response = await api.post('/hospitals', hospitalData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create hospital');
      }
    },

    update: async (id, hospitalData) => {
      try {
        const response = await api.patch(`/hospitals/${id}`, hospitalData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update hospital');
      }
    },

    delete: async (id) => {
      try {
        const response = await api.delete(`/hospitals/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete hospital');
      }
    }
  },

  doctors: {
    getAll: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await api.get(`/doctors${queryString ? `?${queryString}` : ''}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch doctors');
      }
    },

    getSubscription: async () => {
    try {
      const response = await api.get('/doctors/subscription');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
    }
  },
  // Add these to apiService.doctors in api.js

subscribe: async (subscriptionData) => {
  try {
    const response = await api.post('/doctors/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to process subscription');
  }
},

getSubscriptionHistory: async () => {
  try {
    const response = await api.get('/doctors/subscription-history');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription history');
  }
},

getConsultations: async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/doctors/consultations${queryString ? `?${queryString}` : ''}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch consultations');
  }
},

acceptConsultation: async (id, scheduledAt) => {
  try {
    const response = await api.put(`/doctors/consultations/${id}/accept`, { scheduledAt });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to accept consultation');
  }
},

rejectConsultation: async (id, reason) => {
  try {
    const response = await api.put(`/doctors/consultations/${id}/reject`, { reason });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reject consultation');
  }
},

completeConsultation: async (id, completionData) => {
  try {
    const response = await api.put(`/doctors/consultations/${id}/complete`, completionData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to complete consultation');
  }
},

getStats: async () => {
  try {
    const response = await api.get('/doctors/stats');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch doctor statistics');
  }
},

getEarnings: async (startDate, endDate) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/doctors/earnings${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch earnings report');
  }
},
 getById: async (id) => {
      try {
        const response = await api.get(`/doctors/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch doctor');
      }
    },

    search: async (query, filters = {}) => {
      try {
        const params = new URLSearchParams({ q: query, ...filters });
        const response = await api.get(`/doctors/search?${params}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to search doctors');
      }
    },

    getProfile: async () => {
      try {
        const response = await api.get('/doctors/profile');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch doctor profile');
      }
    },

    updateProfile: async (profileData) => {
      try {
        const response = await api.put('/doctors/profile', profileData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update doctor profile');
      }
    },

    getAvailability: async (doctorId, date) => {
      try {
        const response = await api.get(`/doctors/${doctorId}/availability?date=${date}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch availability');
      }
    },

    updateAvailability: async (availability) => {
      try {
        const response = await api.put('/doctors/availability', availability);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update availability');
      }
    }
  },

  ambulances: {
    createProfile: async (profileData) => {
    try {
      const response = await api.post('/ambulances/me/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create profile');
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/ambulances/me/profile');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, needsProfile: true };
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch profile');
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/ambulances/me/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ambulance services');
    }
  },

  
  getById: async (id) => {
    try {
      const response = await api.get(`/ambulances/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ambulance service');
    }
  },

 
getSubscriptionPlans: async () => {
  try {
    const response = await api.get('/ambulances/subscription/plans');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription plans');
  }
},

getSubscription: async () => {
  try {
    const response = await api.get('/ambulances/subscription');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
  }
},
subscribe: async (subscriptionData) => {
  try {
    const response = await api.post('/ambulances/subscription/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Subscription failed');
  }
},
cancelSubscription: async () => {
  try {
    const response = await api.post('/ambulances/subscription/cancel');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
  }
},


getSubscriptionHistory: async () => {
  try {
    const response = await api.get('/ambulances/subscription/history');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription history');
  }
},
  
  getNearby: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/nearby?${queryString}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch nearby ambulances');
    }
  },
  
  getVehicles: async () => {
    try {
      const response = await api.get('/ambulances/me/vehicles');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch vehicles');
    }
  },

  
  addVehicle: async (vehicleData) => {
    try {
      const response = await api.post('/ambulances/me/vehicles', vehicleData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add vehicle');
    }
  },

  
  updateVehicle: async (vehicleId, vehicleData) => {
    try {
      const response = await api.put(`/ambulances/me/vehicles/${vehicleId}`, vehicleData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update vehicle');
    }
  },

  
  deleteVehicle: async (vehicleId) => {
    try {
      const response = await api.delete(`/ambulances/me/vehicles/${vehicleId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete vehicle');
    }
  },

  
  updateVehicleLocation: async (vehicleId, locationData) => {
    try {
      const response = await api.patch(`/ambulances/me/vehicles/${vehicleId}/location`, locationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update vehicle location');
    }
  },

  
  updateVehicleStatus: async (vehicleId, status) => {
    try {
      const response = await api.patch(`/ambulances/me/vehicles/${vehicleId}/status`, { status });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update vehicle status');
    }
  },

 
  getDrivers: async () => {
    try {
      const response = await api.get('/ambulances/me/drivers');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch drivers');
    }
  },

  
  addDriver: async (driverData) => {
    try {
      const response = await api.post('/ambulances/me/drivers', driverData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add driver');
    }
  },

 
  updateDriver: async (driverId, driverData) => {
    try {
      const response = await api.put(`/ambulances/me/drivers/${driverId}`, driverData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update driver');
    }
  },

  deleteDriver: async (driverId) => {
    try {
      const response = await api.delete(`/ambulances/me/drivers/${driverId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete driver');
    }
  },

  
  updateDriverStatus: async (driverId, status) => {
    try {
      const response = await api.patch(`/ambulances/me/drivers/${driverId}/status`, { status });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update driver status');
    }
  },

  
  assignDriverToVehicle: async (driverId, vehicleId) => {
    try {
      const response = await api.patch(`/ambulances/me/drivers/${driverId}/assign`, { vehicleId });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to assign driver');
    }
  },

 
  getStats: async () => {
    try {
      const response = await api.get('/ambulances/me/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  },

  
  getAnalytics: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/me/analytics${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch analytics');
    }
  },

  
  getRequests: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/me/requests${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch requests');
    }
  },

  
  getRequestById: async (requestId) => {
    try {
      const response = await api.get(`/ambulances/me/requests/${requestId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch request');
    }
  },

  
  acceptRequest: async (requestId, data = {}) => {
    try {
      const response = await api.put(`/ambulances/me/requests/${requestId}/accept`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to accept request');
    }
  },

  
  rejectRequest: async (requestId, reason) => {
    try {
      const response = await api.put(`/ambulances/me/requests/${requestId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject request');
    }
  },

  
  completeRequest: async (requestId, completionData) => {
    try {
      const response = await api.put(`/ambulances/me/requests/${requestId}/complete`, completionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to complete request');
    }
  },

  
  updateRequestStatus: async (requestId, statusData) => {
    try {
      const response = await api.patch(`/ambulances/me/requests/${requestId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update request status');
    }
  },
  getEarnings: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/me/earnings${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch earnings');
    }
  },
  getPaymentHistory: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/me/payments${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payment history');
    }
  },

  
  uploadProfileImage: async (formData) => {
    try {
      const response = await api.post('/ambulances/me/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload profile image');
    }
  },

 
  uploadVehicleImage: async (vehicleId, formData) => {
    try {
      const response = await api.post(`/ambulances/me/vehicles/${vehicleId}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload vehicle image');
    }
  },

  
  uploadDriverImage: async (driverId, formData) => {
    try {
      const response = await api.post(`/ambulances/me/drivers/${driverId}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload driver image');
    }
  },

  
  getReviews: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/ambulances/me/reviews${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch reviews');
    }
  }
},

subscriptions: {
  getPlans: async () => {
    try {
      const response = await api.get('/subscriptions/plans');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch plans');
    }
  },

  getHospitalSubscription: async (hospitalId) => {
    try {
      const response = await api.get(`/subscriptions/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
    }
  },

  subscribe: async (subscriptionData) => {
    try {
      const response = await api.post('/subscriptions/subscribe', subscriptionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Subscription failed');
    }
  },

  cancel: async (subscriptionId) => {
    try {
      const response = await api.post(`/subscriptions/${subscriptionId}/cancel`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  },

  getMySubscription: async () => {
    try {
      const response = await api.get('/subscriptions/my-subscription');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
    }
  },

  checkConsultationLimit: async () => {
    try {
      const response = await api.get('/subscriptions/check-consultation-limit');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check limit');
    }
  },

  incrementUsage: async () => {
    try {
      const response = await api.post('/subscriptions/increment-usage');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to increment usage');
    }
  }
},

  payments: {
  initiateMpesa: async (paymentData) => {
    try {
      const response = await api.post('/payments/initiate-mpesa', paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to initiate M-Pesa payment');
    }
  },

  checkStatus: async (checkoutRequestId) => {
    try {
      const response = await api.get(`/payments/check-status/${checkoutRequestId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check payment status');
    }
  },

  getHistory: async (hospitalId) => {
    try {
      const response = await api.get(`/payments/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payment history');
    }
  }
},

  photos: {
    uploadHospitalPhotos: async (formData) => {
      try {
        const response = await api.post('/upload/hospital-photos', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Photo upload failed');
      }
    },

    getHospitalPhotos: async () => {
      try {
        const response = await api.get('/upload/hospital-photos');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch photos');
      }
    },

    deleteHospitalPhoto: async (uploadId) => {
      try {
        const response = await api.delete(`/upload/hospital-photos/${uploadId}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete photo');
      }
    }
  },

  consultations: {
    create: async (consultationData) => {
      try {
        const response = await api.post('/consultations', consultationData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create consultation');
      }
    },

    getAll: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await api.get(`/consultations${queryString ? `?${queryString}` : ''}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch consultations');
      }
    },

   getHistory: async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    
    const response = await api.get(`/consultations/patient/history${queryString ? `?${queryString}` : ''}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch consultation history');
  }
},

  rate: async (id, ratingData) => {
    try {
      const response = await api.post(`/consultations/${id}/rate`, ratingData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to rate consultation');
    }
  },

    getById: async (id) => {
      try {
        const response = await api.get(`/consultations/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch consultation');
      }
    },

    update: async (id, updateData) => {
      try {
        const response = await api.put(`/consultations/${id}`, updateData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update consultation');
      }
    },

    cancel: async (id, reason) => {
      try {
        const response = await api.put(`/consultations/${id}/cancel`, { reason });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to cancel consultation');
      }
    },

    complete: async (id, completionData) => {
      try {
        const response = await api.put(`/consultations/${id}/complete`, completionData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to complete consultation');
      }
    }
  },

  patients: {
  // Profile Management
  getProfile: async () => {
    try {
      const response = await api.get('/patients/profile');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Patient profile not found',
          data: null,
          needsRegistration: true
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch patient profile');
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/patients/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/patients/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch patient');
    }
  },

  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/patients${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch patients');
    }
  },

  // Medical History
  addAllergy: async (allergyData) => {
    try {
      const response = await api.post('/patients/profile/allergies', allergyData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add allergy');
    }
  },

  removeAllergy: async (allergyId) => {
    try {
      const response = await api.delete(`/patients/profile/allergies/${allergyId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove allergy');
    }
  },

  addMedicalCondition: async (conditionData) => {
    try {
      const response = await api.post('/patients/profile/medical-history', conditionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add medical condition');
    }
  },
  /**
   * Get complete medical history
   * @returns {Promise} Medical history data
   */
  getMedicalHistory: async () => {
    try {
      const response = await api.get('/patients/medical-history');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch medical history');
    }
  },

  /**
   * Add surgery record
   * @param {Object} surgeryData - Surgery information
   * @returns {Promise} Updated surgeries list
   */
  addSurgery: async (surgeryData) => {
    try {
      const response = await api.post('/patients/profile/surgeries', surgeryData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add surgery');
    }
  },

  /**
   * Add/update family history
   * @param {Object} familyHistoryData - Family history information
   * @returns {Promise} Updated family history
   */
  updateFamilyHistory: async (familyHistoryData) => {
    try {
      const response = await api.put('/patients/profile/family-history', familyHistoryData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update family history');
    }
  },

  // Medications
  addMedication: async (medicationData) => {
    try {
      const response = await api.post('/patients/profile/medications', medicationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add medication');
    }
  },

  stopMedication: async (medicationId) => {
    try {
      const response = await api.patch(`/patients/profile/medications/${medicationId}/stop`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to stop medication');
    }
  },

  getMedicationRefills: async (daysAhead = 7) => {
    try {
      const response = await api.get(`/patients/profile/medications/refills?daysAhead=${daysAhead}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch medication refills');
    }
  },

  // Vitals
  updateVitals: async (vitalsData) => {
    try {
      const response = await api.put('/patients/profile/vitals', vitalsData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update vitals');
    }
  },

  // Immunizations
  addImmunization: async (immunizationData) => {
    try {
      const response = await api.post('/patients/profile/immunizations', immunizationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add immunization');
    }
  },

  getUpcomingImmunizations: async () => {
    try {
      const response = await api.get('/patients/profile/immunizations/upcoming');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch upcoming immunizations');
    }
  },

  // Insurance
  addInsurance: async (insuranceData) => {
    try {
      const response = await api.post('/patients/profile/insurance', insuranceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add insurance');
    }
  },

  updateInsurance: async (insuranceId, insuranceData) => {
    try {
      const response = await api.put(`/patients/profile/insurance/${insuranceId}`, insuranceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update insurance');
    }
  },

  removeInsurance: async (insuranceId) => {
    try {
      const response = await api.delete(`/patients/profile/insurance/${insuranceId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove insurance');
    }
  },

  // Documents
  uploadDocument: async (documentData) => {
    try {
      const response = await api.post('/patients/profile/documents', documentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload document');
    }
  },

  deleteDocument: async (documentId) => {
    try {
      const response = await api.delete(`/patients/profile/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete document');
    }
  },

  // Health Summary
  getHealthSummary: async () => {
    try {
      const response = await api.get('/patients/profile/health-summary');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch health summary');
    }
  },

  // Preferred Providers
  updatePreferredProviders: async (providersData) => {
    try {
      const response = await api.put('/patients/profile/preferred-providers', providersData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update preferred providers');
    }
  },

  // Statistics
  getStatistics: async () => {
    try {
      const response = await api.get('/patients/statistics/overview');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  },

  searchByBloodGroup: async (bloodGroup) => {
    try {
      const response = await api.get(`/patients/search/blood-group/${bloodGroup}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search by blood group');
    }
  },

  // Dashboard
  getDashboard: async () => {
    try {
      const response = await api.get('/patients/dashboard');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard data');
    }
  },

  // Notifications
  getNotifications: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/patients/notifications${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch notifications');
    }
  },

  markNotificationRead: async (notificationId) => {
    try {
      const response = await api.patch(`/patients/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark notification as read');
    }
  },

  markAllNotificationsRead: async () => {
    try {
      const response = await api.patch('/patients/notifications/read-all');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  }
},

pharmacies: {
  // Profile Management (for pharmacy users)
  getProfile: async () => {
    try {
      const response = await api.get('/pharmacies/profile');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Pharmacy profile not found',
          data: null,
          needsRegistration: true
        };
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch pharmacy profile');
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/pharmacies/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update pharmacy profile');
    }
  },
   uploadProfileImage: async (formData) => {
    try {
      const response = await api.post('/pharmacies/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload profile image');
    }
  },
// Subscription endpoints
getSubscriptionPlans: async () => {
  try {
    const response = await api.get('/pharmacies/subscription/plans');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch plans');
  }
},

getSubscription: async () => {
  try {
    const response = await api.get('/pharmacies/subscription');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
  }
},

subscribe: async (subscriptionData) => {
  try {
    const response = await api.post('/pharmacies/subscription/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Subscription failed');
  }
},

cancelSubscription: async () => {
  try {
    const response = await api.post('/pharmacies/subscription/cancel');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
  }
},
  // Product Management
  getProducts: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/pharmacies/products${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  },

  createProduct: async (productData) => {
    try {
      const response = await api.post('/pharmacies/products', productData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create product');
    }
  },

  updateProduct: async (id, productData) => {
    try {
      const response = await api.put(`/pharmacies/products/${id}`, productData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update product');
    }
  },

  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`/pharmacies/products/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
  },

  uploadProductImage: async (id, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await api.post(`/pharmacies/products/${id}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload product image');
    }
  },

  // Order Management
  getOrders: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/pharmacies/orders${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch orders');
    }
  },

  updateOrderStatus: async (id, statusData) => {
    try {
      const response = await api.put(`/pharmacies/orders/${id}/status`, statusData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update order status');
    }
  },

  // Statistics
  getStats: async () => {
    try {
      const response = await api.get('/pharmacies/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  },

  // Public Endpoints (for browsing pharmacies)
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/pharmacies${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pharmacies');
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/pharmacies/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pharmacy');
    }
  },

  getNearby: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/pharmacies/nearby?${queryString}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch nearby pharmacies');
    }
  },

  search: async (query, filters = {}) => {
    try {
      const params = new URLSearchParams({ q: query, ...filters });
      const response = await api.get(`/pharmacies/search?${params}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search pharmacies');
    }
  }
},

medicines: {
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/medicines${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch medicines');
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/medicines/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch medicine');
    }
  },

  search: async (query, filters = {}) => {
    try {
      const params = new URLSearchParams({ q: query, ...filters });
      const response = await api.get(`/medicines/search?${params}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search medicines');
    }
  }
},

orders: {
  create: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create order');
    }
  },

  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/orders${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch orders');
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order');
    }
  },

  update: async (id, updateData) => {
    try {
      const response = await api.put(`/orders/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update order');
    }
  },

  cancel: async (id, reason) => {
    try {
      const response = await api.put(`/orders/${id}/cancel`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel order');
    }
  },

  rate: async (id, ratingData) => {
    try {
      const response = await api.post(`/orders/${id}/rate`, ratingData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to rate order');
    }
  }
},

  inventory: {
    getAll: async (params = {}) => {
      try {
        const response = await api.get('/inventory', { params });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch inventory');
      }
    },

    getStats: async () => {
      try {
        const response = await api.get('/inventory/stats');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch stats');
      }
    },

    create: async (itemData) => {
      try {
        const response = await api.post('/inventory', itemData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create item');
      }
    },

    update: async (id, itemData) => {
      try {
        const response = await api.put(`/inventory/${id}`, itemData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update item');
      }
    },

    delete: async (id) => {
      try {
        const response = await api.delete(`/inventory/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete item');
      }
    },

    addTransaction: async (id, transactionData) => {
      try {
        const response = await api.post(`/inventory/${id}/transaction`, transactionData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to record transaction');
      }
    }
  },

  admissions: {
    getAll: async (params = {}) => {
      try {
        const response = await api.get('/admissions', { params });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch admissions');
      }
    },

    getStats: async () => {
      try {
        const response = await api.get('/admissions/stats');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch stats');
      }
    },

    create: async (admissionData) => {
      try {
        const response = await api.post('/admissions', admissionData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create admission');
      }
    },

    update: async (id, updateData) => {
      try {
        const response = await api.put(`/admissions/${id}`, updateData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update admission');
      }
    },

    addVitals: async (id, vitalsData) => {
      try {
        const response = await api.post(`/admissions/${id}/vitals`, vitalsData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to add vitals');
      }
    },

    discharge: async (id, dischargeData) => {
      try {
        const response = await api.post(`/admissions/${id}/discharge`, dischargeData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to discharge patient');
      }
    }
  },

notifications: {
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/notifications${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch notifications');
    }
  },

  getUnreadCount: async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch unread count');
    }
  },

  markAsRead: async (notificationId) => {
    try {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark as read');
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await api.patch('/notifications/read-all');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark all as read');
    }
  },

  deleteNotification: async (notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete notification');
    }
  }
}
};

export default apiService;